const $ = require("jquery");
const ko = require("knockout");
const ChatEventViewModel = require('./chat-event.vm');
const Dialog = require("./dialog");
require("../../scripts/custom-ko-functions");

// sessionStorage key for persisting the chat session ID across page navigations. This is reset when starting a new chat (via new chat button) or when the browser tab is closed and reopened.
const CHAT_SESSION_STORAGE_KEY = "wsgpt_chat_session_id";
const CHAT_LOG_STORAGE_KEY = "wsgpt_chat_log";
const CHAT_OPEN_STORAGE_KEY = "wsgpt_chat_open";
const CHAT_NOTIFICATION_STATE_STORAGE_KEY = "wsgpt_chat_notification_state";

function getOrCreateSessionId() {
    let storedId = sessionStorage.getItem(CHAT_SESSION_STORAGE_KEY);
    if (storedId) {
        return storedId;
    }
    let newId = crypto.randomUUID();
    sessionStorage.setItem(CHAT_SESSION_STORAGE_KEY, newId);
    return newId;
}

function saveChatLog(chatLogData) {
    try {
        sessionStorage.setItem(CHAT_LOG_STORAGE_KEY, JSON.stringify(chatLogData));
    } catch (e) {
        // Storage full or unavailable — chat still works, just won't persist across pages
    }
}

function loadChatLog() {
    try {
        let stored = sessionStorage.getItem(CHAT_LOG_STORAGE_KEY);
        let parsed = stored ? JSON.parse(stored) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
}

function isValidChatEvent(eventData) {
    return eventData
        && typeof eventData === "object"
        && typeof eventData.participantId === "string"
        && eventData.participantId.trim() !== ""
        && typeof eventData.message === "string"
        && eventData.message.trim() !== "";
}

function ChatViewModel($el) {
    let self = this;

    self.$dialog = $el.closest(".ws-dialog.ws-chat-dialog");
    self.$chatLog = $el.find(".ws-chat__log-container");
    self.$sendMessageInput = $el.find(".ws-chat__send-message-input");
    self.$sendMessageButton = $el.find(".ws-chat__send-message-button");

    self.sessionId = getOrCreateSessionId();
    // Expose the session id to chat-analytics.js so it can correlate GTM events.
    self.$dialog.attr("data-conversation-id", self.sessionId);
    self.loadingChat = ko.observable(false);
    self.chatLog = ko.observableArray([]);
    self.currentMessage = ko.observable();
    self.isAgentTyping = ko.observable(false);
    self.chatInactive = ko.observable(false);
    self.awaitingResponse = ko.observable(false);

    const $notification = $(".ws-chat__notification");
    const notificationNotifyingClass = "ws-chat__notification--notifying";
    let notificationTimer;

    // Identifier used to distinguish user messages in ChatEventViewModel
    let userParticipantId = "user";

    // Stores raw event data for sessionStorage serialization
    let chatLogData = loadChatLog().filter(isValidChatEvent);
    if (chatLogData.length > 0) {
        saveChatLog(chatLogData);
    }

    chatLogData.forEach(function (eventData) {
        self.chatLog.push(new ChatEventViewModel(eventData, userParticipantId));
    });

    self.sendMessageDisabled = ko.computed(function () {
        return self.loadingChat() || self.chatInactive() || self.awaitingResponse();
    });

    self.handleKeydown = function (data, event) {
        if (event.keyCode === 13 && !event.shiftKey) {
            event.preventDefault();
            self.sendMessage();
        }
        return true;
    };

    self.sendMessage = function () {
        if (self.sendMessageDisabled()) {
            return;
        }

        let messageToSend = self.currentMessage();
        if (!messageToSend || !messageToSend.trim()) {
            return;
        }

        // Add user message to chat log immediately
        addChatEvent(userParticipantId, "You", messageToSend);

        // Clear input
        self.currentMessage("");
        let inputEl = self.$sendMessageInput[0];
        inputEl.style.height = "auto";
        inputEl.style.height = inputEl.scrollHeight + "px";

        // Show typing indicator while waiting for AI response
        self.isAgentTyping(true);
        self.awaitingResponse(true);
        scrollChatToBottom();

        $.ajax({
            type: "POST",
            contentType: "application/json",
            data: JSON.stringify({ message: messageToSend, sessionId: self.sessionId }),
            url: "/api/sitecore/ChatApi/SendChatbotMessage",
            success: function (response) {
                if (response.success) {
                    addChatEvent("assistant", "Assistant", response.message);
                } else {
                    addChatEvent("system", "System", response.error && response.error.trim() ? response.error : "An error occurred. Please try again.");
                }
            },
            error: function () {
                addChatEvent("system", "System", "Oops, our chat service is unavailable at this time. Please try again later.");
            },
            complete: function () {
                self.isAgentTyping(false);
                self.awaitingResponse(false);
            }
        });
    };

    function scrollChatToBottom() {
        if (self.$chatLog.length) {
            setTimeout(function () {
                self.$chatLog.scrollTop(self.$chatLog[0].scrollHeight);
            }, 0);
        }
    }

    function addChatEvent(eventParticipantId, displayName, message) {
        let eventData = {
            participantId: eventParticipantId,
            displayName: displayName,
            message: message,
            serverUnixTimestamp: Date.now()
        };

        if (!isValidChatEvent(eventData)) {
            return;
        }

        chatLogData.push(eventData);
        let chatEvent = new ChatEventViewModel(eventData, userParticipantId);
        self.chatLog.push(chatEvent);
        saveChatLog(chatLogData);
    }

    self.$dialog.on(Dialog.event.Show, function () {
        sessionStorage.setItem(CHAT_OPEN_STORAGE_KEY, "true");
        sessionStorage.setItem(CHAT_NOTIFICATION_STATE_STORAGE_KEY, "dismissed");
        clearNotification();
        self.loadingChat(false);
        self.chatInactive(false);

        if (self.chatLog().length > 0) {
            scrollChatToBottom();
        }
    });

    self.$dialog.on(Dialog.event.Hide, function () {
        sessionStorage.removeItem(CHAT_OPEN_STORAGE_KEY);
    });

    function clearNotification() {
        if (notificationTimer) {
            clearTimeout(notificationTimer);
            notificationTimer = null;
        }
        $notification.removeClass(notificationNotifyingClass);
    }

    function startNotificationTimer() {
        if (!$notification.length) {
            return;
        }

        const notificationSeconds = parseInt($notification.attr("data-ws-chat-notification-timer"), 10);
        const notificationState = sessionStorage.getItem(CHAT_NOTIFICATION_STATE_STORAGE_KEY);
        let hasIndicator = $notification.attr("data-ws-chat-notification-indicator") === "True";
        let hasPopup = $notification.attr("data-ws-chat-notification-popup") === "True";

        if ((!hasIndicator && !hasPopup)
            || sessionStorage.getItem(CHAT_OPEN_STORAGE_KEY) === "true"
            || notificationState === "dismissed") {
            return;
        }

        if (notificationState === "active") {
            $notification.addClass(notificationNotifyingClass);
            return;
        }

        if (!notificationSeconds || notificationSeconds < 1) {
            return;
        }

        notificationTimer = setTimeout(function () {
            if ($notification.length && sessionStorage.getItem(CHAT_OPEN_STORAGE_KEY) !== "true") {
                $notification.addClass(notificationNotifyingClass);
                sessionStorage.setItem(CHAT_NOTIFICATION_STATE_STORAGE_KEY, "active");
            }
        }, notificationSeconds * 1000);
    }

    startNotificationTimer();

    self.startNewChat = function () {
        // Clear the stored session and conversation so the next chat is fresh
        sessionStorage.removeItem(CHAT_SESSION_STORAGE_KEY);
        sessionStorage.removeItem(CHAT_LOG_STORAGE_KEY);
        self.sessionId = getOrCreateSessionId();
        self.$dialog.attr("data-conversation-id", self.sessionId);
        chatLogData.length = 0;
        self.chatLog([]);
        self.currentMessage("");
        let inputEl = self.$sendMessageInput[0];
        inputEl.style.height = "auto";
        inputEl.style.height = inputEl.scrollHeight + "px";
        self.isAgentTyping(false);
        self.awaitingResponse(false);
        self.chatInactive(false);
    };

    $el.on("click", ".ws-chat__new-chat-button", function (event) {
        event.preventDefault();
        self.startNewChat();
    });

    // Re-open the dialog on page load if it was open when the user navigated away.
    // setTimeout defers until after dialog.js's $(document).ready() has initialized its internals.
    if (sessionStorage.getItem(CHAT_OPEN_STORAGE_KEY) === "true") {
        setTimeout(function () { Dialog.openDialog(self.$dialog); }, 0);
    }

    // Send a starter question when clicked
    $el.on("click", ".ws-chat__starter-question .ws-chat__log-message-group", function () {
        let questionText = $(this).find(".ws-chat__log-message-text").text().trim();
        if (questionText && !self.sendMessageDisabled()) {
            self.currentMessage(questionText);
            self.sendMessage();
        }
    });

    $el.on("keydown", ".ws-chat__starter-question .ws-chat__log-message-group", function (event) {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            $(this).trigger("click");
        }
    });

}

module.exports = ChatViewModel;